// Mobile Draft screen — over ZenGM's draft view. Two toggleable views:
//   • Draft order — one continuous, live pick-by-pick list. Your slot expands inline to a
//     prospect picker.
//   • Big Board — a horizontally-scrolling prospect table with measurables + extended
//     ratings, sortable columns, and position filters; draft directly from it.
// Real data: `undrafted`/`drafted`/`teamsByTid` from the draft worker view; drafting uses
// `draftUser`. The draft calendar is advanced by the app-shell advance bar
// (GmShell: "Sim to my pick" → untilYourNextPick), not by this view.

import { useEffect, useRef, useState } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { useLocal } from "../util/local.ts";
import { toWorker } from "../util/toWorker.ts";
import { helpers } from "../util/helpers.ts";
import { realtimeUpdate } from "../util/realtimeUpdate.ts";
import { PHASE } from "../../common/constants.ts";
import { getHeightString } from "../components/Height.tsx";
import type { View } from "../../common/types.ts";

const TEAL = "#18E0A8";

const POS_COLORS: Record<string, string> = {
	PG: "#4A9EFF",
	SG: "#2f9fe0",
	SF: "#18E0A8",
	PF: "#E8B923",
	C: "#E2504E",
	GF: "#7C9FF0",
	FC: "#E8A020",
	F: "#9a6cff",
	G: "#4A9EFF",
};
const posColor = (pos: string) => POS_COLORS[pos] ?? "#4A9EFF";
const ratingColor = (v: number) =>
	v >= 62 ? TEAL : v >= 48 ? "#E8B923" : "#8f9aa2";

// Big Board columns. `key` doubles as the sort key; `get` reads the display/sort value
// from a prospect's flat ratings object. POS is filter-only (not sortable).
type Col = {
	key: string;
	label: string;
	sort: boolean;
	get: (p: any) => number | string;
};
const COLS: Col[] = [
	{ key: "pos", label: "POS", sort: false, get: (p) => p.ratings.pos },
	{ key: "age", label: "AGE", sort: true, get: (p) => p.age },
	{ key: "hgt", label: "HT", sort: true, get: (p) => p.hgt },
	{ key: "weight", label: "WT", sort: true, get: (p) => p.weight },
	{ key: "ovr", label: "OVR", sort: true, get: (p) => p.ratings.ovr },
	{ key: "pot", label: "POT", sort: true, get: (p) => p.ratings.pot },
	{ key: "ins", label: "INS", sort: true, get: (p) => p.ratings.ins },
	{ key: "tp", label: "3PT", sort: true, get: (p) => p.ratings.tp },
	{ key: "fg", label: "MID", sort: true, get: (p) => p.ratings.fg },
	{
		key: "ply",
		label: "PLY",
		sort: true,
		get: (p) => Math.round((p.ratings.pss + p.ratings.oiq) / 2),
	},
	{ key: "diq", label: "DEF", sort: true, get: (p) => p.ratings.diq },
	{ key: "reb", label: "REB", sort: true, get: (p) => p.ratings.reb },
	{
		key: "ath",
		label: "ATH",
		sort: true,
		get: (p) => Math.round((p.ratings.spd + p.ratings.jmp) / 2),
	},
];
// Columns whose cells get rating-tier coloring (the skill/overall ratings, not measurables).
const RATING_KEYS = new Set([
	"ovr",
	"pot",
	"ins",
	"tp",
	"fg",
	"ply",
	"diq",
	"reb",
	"ath",
]);

const FILTERS: { key: string; label: string; match: (pos: string) => boolean }[] =
	[
		{ key: "All", label: "All", match: () => true },
		{
			key: "G",
			label: "Guards",
			match: (pos) => ["PG", "SG", "G", "GF"].includes(pos),
		},
		{
			key: "F",
			label: "Forwards",
			match: (pos) => ["SF", "PF", "F", "GF", "FC"].includes(pos),
		},
		{ key: "C", label: "Centers", match: (pos) => ["C", "FC"].includes(pos) },
	];

// Draft board reveal: as the sim lands picks, uncover them one at a time (scrolling to each)
// instead of dumping the whole batch, so "Sim to my pick" plays out like a live draft.
const DRAFT_REVEAL_MS = 320;

const GmDraft = ({
	drafted,
	undrafted,
	teamsByTid,
	fantasyDraft,
}: View<"draft">) => {
	useTitleBar({ title: "Draft" });
	const { userTids, units, phase, lid } = useLocal([
		"userTids",
		"units",
		"phase",
		"lid",
	]);

	const [view, setView] = useState<"order" | "best">("order");
	const [bestSort, setBestSort] = useState("pot");
	const [bestDir, setBestDir] = useState(-1); // -1 desc, 1 asc
	const [posFilter, setPosFilter] = useState("All");
	const [drafting, setDrafting] = useState(false);
	const [toast, setToast] = useState("");

	const draftedArr = drafted as any[];
	const undraftedArr = undrafted as any[];
	const teams = teamsByTid as Record<number, any>;

	const isMine = (tid: number | undefined) =>
		tid !== undefined && userTids.includes(tid);
	const teamName = (tid: number) => {
		const t = teams[tid];
		return t ? t.region || t.abbrev : `Team ${tid}`;
	};
	const teamAbbr = (tid: number) => teams[tid]?.abbrev ?? "—";
	const teamColor = (tid: number) => teams[tid]?.colors?.[0] ?? "#4A9EFF";
	// The team that "owns" a slot: for a filled pick, the player's current team (so a
	// traded-for player re-badges to us); for a pending pick, the draft-pick owner.
	const slotTid = (p: any) => (p.pid >= 0 ? p.tid : p.draft.tid);

	// --- draft clock ---
	const nextIdx = draftedArr.findIndex((p) => p.pid < 0);
	const complete = nextIdx === -1;
	const nextPick = complete ? undefined : draftedArr[nextIdx];

	// Number of slots that have actually been drafted (all filled slots precede nextIdx). The
	// reveal animation walks revealedFilled up to this, uncovering one pick per tick. Initialized
	// to the current count so revisiting a draft in progress doesn't re-animate from scratch.
	const targetFilled = complete ? draftedArr.length : nextIdx;
	const targetRef = useRef(targetFilled);
	targetRef.current = targetFilled;
	const revealedRef = useRef(targetFilled);
	const [revealedFilled, setRevealedFilled] = useState(targetFilled);
	const revealTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		// New/older data with fewer filled slots (e.g. navigating in): snap, don't animate.
		if (targetFilled < revealedRef.current) {
			revealedRef.current = targetFilled;
			setRevealedFilled(targetFilled);
			return;
		}
		// More picks have landed than we've revealed — start (or let the running timer continue,
		// via targetRef) uncovering them one at a time, scrolling to each as it appears.
		if (targetFilled > revealedRef.current && !revealTimerRef.current) {
			revealTimerRef.current = setInterval(() => {
				if (revealedRef.current >= targetRef.current) {
					if (revealTimerRef.current) {
						clearInterval(revealTimerRef.current);
						revealTimerRef.current = null;
					}
					return;
				}
				revealedRef.current += 1;
				const row = revealedRef.current;
				setRevealedFilled(row);
				requestAnimationFrame(() => {
					document
						.getElementById(`dp-row-${row}`)
						?.scrollIntoView({ behavior: "smooth", block: "center" });
				});
				if (revealedRef.current >= targetRef.current && revealTimerRef.current) {
					clearInterval(revealTimerRef.current);
					revealTimerRef.current = null;
				}
			}, DRAFT_REVEAL_MS);
		}
	}, [targetFilled]);

	useEffect(
		() => () => {
			if (revealTimerRef.current) {
				clearInterval(revealTimerRef.current);
			}
		},
		[],
	);

	// While the reveal is catching up, the "current" card is the slot being uncovered; once caught
	// up it's the real on-the-clock pick (or none when the draft is complete).
	const revealing = revealedFilled < targetFilled;
	const curIdx = revealing ? revealedFilled : complete ? -1 : nextIdx;

	// When the draft finishes, skip the "DRAFT COMPLETE" recap (the full every-pick board) and go
	// straight to re-signing. Fires once, and only once the phase has settled to AFTER_DRAFT so
	// untilResignPlayers is valid.
	const skippedRecap = useRef(false);
	useEffect(() => {
		if (complete && phase === PHASE.AFTER_DRAFT && !skippedRecap.current) {
			skippedRecap.current = true;
			(async () => {
				await toWorker("playMenu", "untilResignPlayers", undefined);
				if (lid !== undefined) {
					realtimeUpdate([], `/l/${lid}`);
				}
			})();
		}
	}, [complete, phase, lid]);
	const onClockTid = nextPick?.draft?.tid;
	const usersTurn = !!(nextPick && isMine(onClockTid));

	// The user's next upcoming pick: first still-pending slot (on the clock or later) that
	// the user owns. Its ordinal targets the `#dp-row-N` element for auto-scroll.
	const myNextIdx =
		nextIdx === -1
			? -1
			: draftedArr.findIndex(
					(p, i) => i >= nextIdx && p.pid < 0 && isMine(p.draft.tid),
				);
	const myNextOrdinal = myNextIdx === -1 ? undefined : myNextIdx + 1;

	// As the draft advances (picks made, order changes), follow along to the user's next pick
	// so they always see where they're up. Keyed on the target's dpid so we only scroll when the
	// target actually changes — this won't fight the user's manual scrolling on unrelated renders.
	const lastScrolledDpid = useRef<number | undefined>(undefined);
	useEffect(() => {
		// While the reveal is running it owns the scroll (following each pick); only settle on the
		// user's next pick once it's done.
		if (view !== "order" || myNextIdx === -1 || revealing) return;
		const dpid = draftedArr[myNextIdx]?.draft?.dpid;
		if (dpid === undefined || dpid === lastScrolledDpid.current) return;
		lastScrolledDpid.current = dpid;
		// Defer to after paint so the row exists / has its final position.
		requestAnimationFrame(() => {
			document
				.getElementById(`dp-row-${myNextIdx + 1}`)
				?.scrollIntoView({ behavior: "smooth", block: "center" });
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [view, myNextIdx, draftedArr, revealing]);

	// Available prospects, best-first (potential, then overall) — powers the inline picker.
	const available = [...undraftedArr].sort(
		(a, b) =>
			b.ratings.pot - a.ratings.pot || b.ratings.ovr - a.ratings.ovr,
	);

	const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined,
	);

	useEffect(() => {
		return () => {
			clearTimeout(toastTimer.current);
		};
	}, []);

	const flash = (msg: string) => {
		clearTimeout(toastTimer.current);
		setToast(msg);
		toastTimer.current = setTimeout(() => setToast(""), 2200);
	};

	// Drafting a player. The draft calendar itself is advanced by the app-shell advance bar
	// ("Sim to my pick" → untilYourNextPick), which lands on the user's slot; the inline picker
	// then expands for the actual selection.
	const draftPlayer = async (pid: number) => {
		if (!usersTurn) {
			flash("Sim to your pick first.");
			return;
		}
		if (drafting) return;
		const p = undraftedArr.find((x) => x.pid === pid);
		setDrafting(true);
		await toWorker("main", "draftUser", pid);
		setDrafting(false);
		// Drop the user onto the live pick stream so the selection (and the sim that follows)
		// plays out there — whether they drafted from the Big Board or the inline picker.
		setView("order");
		flash(`Drafted ${p ? `${p.firstName} ${p.lastName}` : "player"}.`);
		// Keep the draft rolling: sim to the user's next pick so the following selections animate
		// in automatically instead of waiting on another "Sim to my pick" tap.
		void toWorker("playMenu", "untilYourNextPick", undefined);
	};

	// Open a prospect's player card (mobile GmPlayer), so tapping a name on the Big Board scouts
	// the player instead of immediately drafting them.
	const openCard = (pid: number) => {
		realtimeUpdate([], helpers.leagueUrl(["player", pid]));
	};

	// --- Big Board rows ---
	const filter = FILTERS.find((f) => f.key === posFilter) ?? FILTERS[0]!;
	const col = COLS.find((c) => c.key === bestSort) ?? COLS[5]!;
	const boardList = [...undraftedArr]
		.filter((p) => filter.match(p.ratings.pos))
		.sort((a, b) => {
			const av = col.get(a) as number;
			const bv = col.get(b) as number;
			return (bv - av) * (bestDir < 0 ? 1 : -1) || b.ratings.pot - a.ratings.pot;
		});
	const setSort = (key: string) => {
		if (bestSort === key) {
			setBestDir((d) => -d);
		} else {
			setBestSort(key);
			setBestDir(-1);
		}
	};

	const banner = complete
		? {
				grad: "linear-gradient(135deg,#2a2130,#181818)",
				border: "#2a2a2a",
				eyebrow: "DRAFT COMPLETE",
				eyebrowColor: "#c9a4e6",
				title: "That's a wrap",
			}
		: usersTurn
			? {
					grad: "linear-gradient(135deg,#123a5c,#0f2740)",
					border: "#1f5c7a",
					eyebrow: "YOU'RE ON THE CLOCK",
					eyebrowColor: "#7fc4e8",
					title: `Round ${nextPick.draft.round}, Pick ${nextPick.draft.pick}`,
				}
			: {
					grad: "linear-gradient(135deg,#242424,#181818)",
					border: "#2a2a2a",
					eyebrow: "DRAFT IN PROGRESS",
					eyebrowColor: "#9fb0bc",
					title: nextPick
						? `Round ${nextPick.draft.round}, Pick ${nextPick.draft.pick}`
						: "Draft",
				};

	return (
		<div className="gmd">
			<style>{styles}</style>

			{/* status banner */}
			<div
				className="gmd-banner"
				style={{ background: banner.grad, borderColor: banner.border }}
			>
				<div className="gmd-banner-eyebrow" style={{ color: banner.eyebrowColor }}>
					{banner.eyebrow}
				</div>
				<div className="gmd-banner-title">{banner.title}</div>
			</div>

			{/* view toggle */}
			<div className="gmd-tabs">
				<div
					className={`gmd-tab ${view === "order" ? "on" : ""}`}
					onClick={() => setView("order")}
				>
					Draft order
				</div>
				<div
					className={`gmd-tab ${view === "best" ? "on" : ""}`}
					onClick={() => setView("best")}
				>
					Big Board
				</div>
			</div>

			{/* ============ DRAFT ORDER ============ */}
			{view === "order" ? (
				<>
					<div className="gmd-h2">Draft order</div>
					<div className="gmd-order">
						{draftedArr.map((p, i) => {
							const ordinal = i + 1;
							const tid = slotTid(p);
							const me = isMine(tid);
							const isCur = i === curIdx;
							// A slot only shows its player once the reveal has reached it.
							const filled = p.pid >= 0 && i < revealedFilled;
							const expanded = isCur && me && !drafting && !revealing;
							let tint = "gmd-card";
							if (isCur) tint = "gmd-card gmd-card-cur";
							else if (me) tint = "gmd-card gmd-card-mine";
							return (
								<div id={`dp-row-${ordinal}`} className={tint} key={ordinal}>
									<div className="gmd-row-head">
										<div
											className="gmd-num"
											style={{ color: me ? TEAL : "#5f6b73" }}
										>
											{ordinal}
										</div>
										<div
											className="gmd-badge"
											style={{ background: teamColor(tid) }}
										>
											{teamAbbr(tid)}
										</div>
										{filled ? (
											<>
												<div className="gmd-row-main">
													<div className="gmd-row-name">
														{p.firstName} {p.lastName}
													</div>
													<div className="gmd-row-meta">
														{p.ratings.pos} · OVR {p.ratings.ovr} · POT{" "}
														{p.ratings.pot}
													</div>
												</div>
												<div
													className="gmd-posdot"
													style={{ background: posColor(p.ratings.pos) }}
												/>
											</>
										) : (
											<>
												<div className="gmd-row-main">
													<div
														className="gmd-row-status"
														style={{
															color: isCur
																? TEAL
																: me
																	? "#9fe9c8"
																	: "#8a8a8a",
														}}
													>
														{isCur
															? me
																? "ON THE CLOCK"
																: "On the clock…"
															: me
																? "Your upcoming pick"
																: "Upcoming"}
													</div>
													<div className="gmd-row-team">{teamName(tid)}</div>
												</div>
												{isCur ? <div className="gmd-live" /> : null}
											</>
										)}
									</div>

									{expanded ? (
										<div className="gmd-picker">
											<div className="gmd-picker-head">
												<div className="gmd-picker-title">MAKE YOUR PICK</div>
												<div
													className="gmd-picker-full"
													onClick={() => setView("best")}
												>
													Full board →
												</div>
											</div>
											{available.slice(0, 5).map((pr) => (
												<div className="gmd-picker-row" key={pr.pid}>
													<div
														className="gmd-posdot"
														style={{ background: posColor(pr.ratings.pos) }}
													/>
													<div className="gmd-row-main">
														<div className="gmd-row-name">
															{pr.firstName} {pr.lastName}
														</div>
														<div className="gmd-picker-meta">
															{pr.ratings.pos} · Age {pr.age} · OVR{" "}
															{pr.ratings.ovr} · POT {pr.ratings.pot}
														</div>
													</div>
													<div
														className="gmd-draftbtn"
														onClick={() => draftPlayer(pr.pid)}
													>
														Draft
													</div>
												</div>
											))}
										</div>
									) : null}
								</div>
							);
						})}
					</div>
				</>
			) : null}

			{/* ============ BIG BOARD ============ */}
			{view === "best" ? (
				<>
					<div className="gmd-h2">Big board</div>
					<div className="gmd-hint">
						Tap a name to scout · tap Draft to pick · swipe for full ratings ·
						tap a column to sort
					</div>
					<div className="gmd-filters">
						{FILTERS.map((f) => (
							<div
								key={f.key}
								className={`gmd-filter ${posFilter === f.key ? "on" : ""}`}
								onClick={() => setPosFilter(f.key)}
							>
								{f.label}
							</div>
						))}
					</div>
					<div className="gmd-tablewrap">
						<div className="gmd-table">
							<div className="gmd-thead">
								<div className="gmd-th-player">PLAYER</div>
								<div className="gmd-cells">
									{COLS.map((c) => {
										const active = c.sort && bestSort === c.key;
										return (
											<div
												key={c.key}
												className="gmd-th"
												style={{
													color: active ? TEAL : "#7f8b93",
													cursor: c.sort ? "pointer" : "default",
													width: c.key === "hgt" ? 46 : 42,
												}}
												onClick={c.sort ? () => setSort(c.key) : undefined}
											>
												{c.label}
												{active ? (bestDir < 0 ? " ↓" : " ↑") : ""}
											</div>
										);
									})}
								</div>
							</div>
							{boardList.map((p, i) => (
								<div className="gmd-trow" key={p.pid}>
									<div className="gmd-td-player">
										<div className="gmd-trank">{i + 1}</div>
										<div
											className="gmd-posdot"
											style={{ background: posColor(p.ratings.pos) }}
										/>
										<div
											className="gmd-td-info"
											onClick={() => openCard(p.pid)}
										>
											<div className="gmd-row-name">
												{p.firstName} {p.lastName}
											</div>
											<div className="gmd-td-sub">View card ›</div>
										</div>
										<div
											className="gmd-boarddraft"
											onClick={() => draftPlayer(p.pid)}
										>
											Draft
										</div>
									</div>
									<div className="gmd-cells">
										{COLS.map((c) => {
											const raw = c.get(p);
											let v: string | number = raw;
											let color = "#e6e6e6";
											if (c.key === "hgt") {
												v = getHeightString(raw as number, units);
												color = "#cfcfcf";
											} else if (c.key === "pos") {
												color = "#cfcfcf";
											} else if (c.key === "weight") {
												color = "#cfcfcf";
											} else if (RATING_KEYS.has(c.key)) {
												color = ratingColor(raw as number);
											}
											return (
												<span
													key={c.key}
													className="gmd-cell"
													style={{
														color,
														width: c.key === "hgt" ? 46 : 42,
													}}
												>
													{v}
												</span>
											);
										})}
									</div>
								</div>
							))}
						</div>
					</div>
					{boardList.length === 0 ? (
						<div className="gmd-empty">
							No available prospects at this position.
						</div>
					) : null}
				</>
			) : null}

			{/* toast */}
			{toast ? <div className="gmd-toast">{toast}</div> : null}

		</div>
	);
};

const styles = `
.gmd { max-width:520px; margin:0 auto; padding:8px 14px 160px; color:#fff;
  font-family:Montserrat,system-ui,sans-serif; position:relative; }
.gmd ::-webkit-scrollbar { width:0; height:0; }

.gmd-banner { border-radius:16px; padding:16px 18px; margin:6px 2px 0; border:1px solid; }
.gmd-banner-eyebrow { font-size:10.5px; font-weight:900; letter-spacing:1.4px; }
.gmd-banner-title { font-size:23px; font-weight:900; letter-spacing:-.5px; margin-top:4px; }

.gmd-tabs { display:flex; gap:20px; border-bottom:1px solid #232323; margin:16px 2px 0; }
.gmd-tab { padding:10px 0 11px; font-size:14px; font-weight:700; color:#6f7b83;
  border-bottom:2px solid transparent; margin-bottom:-1px; cursor:pointer; }
.gmd-tab.on { color:#fff; font-weight:800; border-bottom-color:#18E0A8; }

.gmd-h2 { padding:18px 2px 2px; font-size:16px; font-weight:900; letter-spacing:-.3px; }
.gmd-hint { padding:1px 2px 0; font-size:10.5px; font-weight:700; color:#6f7b83; }

.gmd-order { display:flex; flex-direction:column; gap:7px; padding:10px 0 0; }
.gmd-card { border-radius:12px; padding:10px 12px; background:#181818; border:1px solid #232323; }
.gmd-card-cur { background:rgba(24,224,168,.1); border-color:rgba(24,224,168,.55); }
.gmd-card-mine { background:#15201d; border-color:#244a3a; }
.gmd-row-head { display:flex; align-items:center; gap:11px; }
.gmd-num { font-size:12px; font-weight:900; width:18px; flex:none; text-align:center; }
.gmd-badge { width:30px; height:30px; border-radius:9px; flex:none; display:flex;
  align-items:center; justify-content:center; font-size:9.5px; font-weight:900; color:#0b0f13; }
.gmd-row-main { flex:1; min-width:0; }
.gmd-row-name { font-size:13px; font-weight:800; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.gmd-row-meta { font-size:10px; color:#8a8a8a; font-weight:700; margin-top:1px; }
.gmd-row-status { font-size:12.5px; font-weight:800; }
.gmd-row-team { font-size:10px; color:#6f7b83; font-weight:700; margin-top:1px; }
.gmd-posdot { width:9px; height:9px; border-radius:50%; flex:none; }
.gmd-live { width:8px; height:8px; border-radius:50%; flex:none; background:#18E0A8;
  animation:gmdpulse 1s ease-in-out infinite; }

.gmd-picker { margin-top:11px; border-top:1px solid rgba(24,224,168,.28); padding-top:11px;
  animation:gmdfade .25s ease both; }
.gmd-picker-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:6px; }
.gmd-picker-title { font-size:11px; font-weight:900; letter-spacing:.4px; color:#18E0A8; }
.gmd-picker-full { font-size:10.5px; font-weight:800; color:#cdd9e2; background:rgba(255,255,255,.08);
  border:1px solid rgba(255,255,255,.14); padding:6px 11px; border-radius:15px; cursor:pointer; }
.gmd-picker-row { display:flex; align-items:center; gap:11px; padding:8px 2px;
  border-top:1px solid rgba(255,255,255,.06); }
.gmd-picker-meta { font-size:10.5px; color:#8fb3c9; font-weight:700; margin-top:1px; }
.gmd-draftbtn { background:#18E0A8; color:#053a2c; font-size:12px; font-weight:900;
  padding:8px 16px; border-radius:20px; cursor:pointer; flex:none; }

.gmd-filters { display:flex; gap:7px; padding:12px 2px 2px; }
.gmd-filter { padding:7px 13px; border-radius:16px; font-size:11.5px; font-weight:800; cursor:pointer;
  background:#1c1c1c; color:#9aa4ac; border:1px solid #2a2a2a; }
.gmd-filter.on { background:#18E0A8; color:#053a2c; border-color:#18E0A8; }

.gmd-tablewrap { overflow-x:auto; margin-top:10px; padding:0 0 4px; -ms-overflow-style:none; scrollbar-width:none; }
.gmd-tablewrap::-webkit-scrollbar { width:0; height:0; }
.gmd-table { min-width:760px; }
.gmd-thead { display:flex; align-items:flex-end; padding-bottom:8px; border-bottom:1px solid #2a2a2a; }
.gmd-th-player { position:sticky; left:0; z-index:2; width:196px; flex:none; background:#121212;
  font-size:8.5px; font-weight:800; color:#6f7b83; letter-spacing:.5px; }
.gmd-cells { display:flex; flex:none; }
.gmd-th { flex:none; text-align:right; font-size:8.5px; font-weight:800; letter-spacing:.3px; }
.gmd-trow { display:flex; align-items:center; padding:9px 0; border-top:1px solid #202020; }
.gmd-td-player { position:sticky; left:0; z-index:2; width:196px; flex:none; background:#121212;
  display:flex; align-items:center; gap:9px; padding-right:10px; }
.gmd-trank { font-size:11px; font-weight:900; color:#5f6b73; width:16px; flex:none; text-align:center; }
.gmd-td-info { flex:1; min-width:0; cursor:pointer; }
.gmd-td-sub { font-size:9.5px; color:#6f7b83; font-weight:700; letter-spacing:.2px; margin-top:1px; }
.gmd-boarddraft { flex:none; background:#18E0A8; color:#053a2c; font-size:11px; font-weight:900;
  padding:7px 14px; border-radius:15px; cursor:pointer; }
.gmd-cell { flex:none; text-align:right; font-size:12px; font-weight:800; }
.gmd-empty { text-align:center; padding:30px 16px; font-size:12.5px; color:#7a7a7a; font-weight:700; }

.gmd-toast { position:fixed; left:50%; bottom:140px; z-index:1020; transform:translateX(-50%);
  background:#fff; color:#000; font-size:12.5px; font-weight:800; padding:11px 18px; border-radius:24px;
  box-shadow:0 10px 30px rgba(0,0,0,.5); white-space:nowrap; animation:gmdflash .3s ease both; }

@keyframes gmdfade { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:none; } }
@keyframes gmdflash { from { opacity:0; transform:translate(-50%,10px); } to { opacity:1; transform:translate(-50%,0); } }
@keyframes gmdpulse { 0%,100% { opacity:1; } 50% { opacity:.45; } }
`;

export default GmDraft;
